/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/scope-ag-grid-pad-decorator.ts
 * FILE_ID: QB-AGGRID-PAD-DECORATOR-001
 * PURPOSE: Decorate a freshly-built AG-Grid ColDef with the SAM-style
 *          pad-row affordances: ghost-text placeholder via
 *          ``valueFormatter`` and the matching ``qb-pad-placeholder``
 *          cellClassRule so the cell renders in italic grey.
 * WHY: Pulled out of scope-ag-grid-columns.ts so that file stays
 *      under the 300-line cap after MR 7 added the new synthetic
 *      checkbox / actions columns.
 * FLOW:
 *   ┌────────────────────┐    ┌───────────────────────┐    ┌────────────────────────┐
 *   │ ColDef built by    │ -> │ wrap valueFormatter   │ -> │ ColDef gains placeholder│
 *   │ buildAgGridColDef  │    │ + add cellClassRules  │    │ behaviour on pad rows   │
 *   └────────────────────┘    └───────────────────────┘    └────────────────────────┘
 * RELATED:
 *   - QB-AGGRID-PAD-ROWS-001 (pad-row-helpers.ts) — getPadRowPlaceholder
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — caller
 *   - QB-AGGRID-STYLES-001 (lazy-aggrid-styles.tsx) — visual CSS
 */

import type { QuoteLineItem } from '@/lib/quoteApi'
import type { AgColDef } from './scope-ag-grid-columns'
import { getPadRowPlaceholder, isPadRowId } from './pad-row-helpers'

/**
 * Mutates ``colDef`` in place to add placeholder + cellClassRules
 * when the column has a configured pad-row placeholder. Returns the
 * same ``colDef`` for fluent chaining.
 *
 * No-op for columns without a placeholder (Item #, outcome columns,
 * custom user-defined columns) — those stay as-is.
 */
export function decorateColDefWithPadPlaceholder(
  colDef: AgColDef,
): AgColDef {
  if (!colDef.colId) return colDef
  const placeholder = getPadRowPlaceholder(colDef.colId)
  if (!placeholder) return colDef

  const innerFormatter = colDef.valueFormatter
  colDef.valueFormatter = ({
    value,
    data,
  }: {
    value: unknown
    data?: QuoteLineItem | null
  }) => {
    const isPadEmpty =
      data?.id !== undefined &&
      isPadRowId(data.id) &&
      (value === null || value === undefined || value === '')
    if (isPadEmpty) return placeholder
    return innerFormatter ? innerFormatter({ value }) : String(value ?? '')
  }
  colDef.cellClassRules = {
    'qb-pad-placeholder': ({
      value,
      data,
    }: {
      value: unknown
      data?: QuoteLineItem | null
    }) =>
      !!(
        data?.id &&
        isPadRowId(data.id) &&
        (value === null || value === undefined || value === '')
      ),
  }
  return colDef
}
